export interface Relationship {
  id: number;
  from_object_id: string;
  from_object_type: string;
  to_object_id: string;
  to_object_type: string;
  relationship_type?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateRelationshipData {
  from_object_id: string;
  from_object_type: string;
  to_object_id: string;
  to_object_type: string;
  relationship_type?: string;
  notes?: string;
}

export interface UpdateRelationshipData {
  relationship_type?: string;
  notes?: string;
}

export interface RelationshipFilters {
  from_object_id?: string;
  from_object_type?: string;
  to_object_id?: string;
  to_object_type?: string;
  relationship_type?: string;
  limit?: number;
  offset?: number;
}

export interface RelationshipStats {
  total: number;
  recent: number;
  byType: Array<{
    from_object_type: string;
    count: number;
  }>;
}

export interface RelationshipResponse {
  success: boolean;
  relationship?: Relationship;
  relationships?: Relationship[];
  data?: {
    relationships?: Relationship[];
    pagination?: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
  stats?: RelationshipStats;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  message?: string;
}

// Common object types that can be related
export enum ObjectType {
  BOOK = 'book',
  GOAL = 'goal',
  HABIT = 'habit',
  TASK = 'task',
  DAILY_NOTE = 'daily_note',
  JOURNEY = 'journey',
  USER = 'user',
  CONCEPT = 'concept',
  MILESTONE = 'milestone',
  BLOCK = 'block'
}

// Relationship types for better semantic meaning
export enum RelationshipType {
  RELATED_TO = 'related_to',
  PREREQUISITE_FOR = 'prerequisite_for',
  SIMILAR_TO = 'similar_to',
  INSPIRED_BY = 'inspired_by',
  BUILDS_ON = 'builds_on',
  CONTRADICTS = 'contradicts',
  SUPPORTS = 'supports',
  CHALLENGES = 'challenges',
  COMPLEMENTS = 'complements',
  REFERENCES = 'references',
  PARENT_CHILD = 'parent_child',
  MILESTONE = 'milestone'
}

// Helper function to get display name for object types
export function getObjectTypeDisplayName(objectType: string): string {
  const displayNames: Record<string, string> = {
    [ObjectType.BOOK]: 'Book',
    [ObjectType.GOAL]: 'Goal',
    [ObjectType.HABIT]: 'Habit',
    [ObjectType.TASK]: 'Task',
    [ObjectType.DAILY_NOTE]: 'Daily Note',
    [ObjectType.JOURNEY]: 'Learning Journey',
    [ObjectType.USER]: 'User',
    [ObjectType.CONCEPT]: 'Concept',
    [ObjectType.MILESTONE]: 'Milestone',
    [ObjectType.BLOCK]: 'Block'
  };
  
  return displayNames[objectType] || objectType;
}

// Helper function to get display name for relationship types
export function getRelationshipTypeDisplayName(relationshipType: string): string {
  const displayNames: Record<string, string> = {
    [RelationshipType.RELATED_TO]: 'Related to',
    [RelationshipType.PREREQUISITE_FOR]: 'Prerequisite for',
    [RelationshipType.SIMILAR_TO]: 'Similar to',
    [RelationshipType.INSPIRED_BY]: 'Inspired by',
    [RelationshipType.BUILDS_ON]: 'Builds on',
    [RelationshipType.CONTRADICTS]: 'Contradicts',
    [RelationshipType.SUPPORTS]: 'Supports',
    [RelationshipType.CHALLENGES]: 'Challenges',
    [RelationshipType.COMPLEMENTS]: 'Complements',
    [RelationshipType.REFERENCES]: 'References',
    [RelationshipType.PARENT_CHILD]: 'Parent/Child',
    [RelationshipType.MILESTONE]: 'Milestone'
  };
  
  return displayNames[relationshipType] || relationshipType;
}
